'use client';

import { useAuth } from '@/backend/context/auth-management';
import { useFinance, useMoney } from '@/backend/firebase/hooks';
import { FormSheetLayout } from '@/components/form-sheet-layout';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { zodResolver } from '@hookform/resolvers/zod';
import { Banknote, Calendar as CalendarIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { format, addDays, startOfDay, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

const expenseFormSchema = z.object({
  category: z.enum([
    'supplies',
    'rent',
    'utilities',
    'payroll',
    'marketing',
    'taxes',
    'other',
  ]),
  description: z.string().min(1, 'La descripción es obligatoria'),
  amount: z.coerce.number().positive('El monto debe ser mayor a 0'),
  currency: z.enum(['USD', 'EUR', 'BS', 'COP']),
  paymentMethodId: z.string().min(1, 'Selecciona un método de pago'),
  date: z.string().min(1, 'Selecciona una fecha'),
});

type ExpenseFormValues = z.infer<typeof expenseFormSchema>;

interface ExpenseModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const categoryLabels: Record<string, string> = {
  supplies: 'Insumos',
  rent: 'Alquiler',
  utilities: 'Servicios',
  payroll: 'Nómina',
  marketing: 'Publicidad',
  taxes: 'Impuestos',
  other: 'Otros gastos',
};

export function ExpenseModal({ open, onOpenChange }: ExpenseModalProps) {
  const { activeBusiness, profile } = useAuth();
  const businessId = activeBusiness?.id || '';
  const isWorker = profile?.account_type === 'professional';

  const [showFullCalendar, setShowFullCalendar] = useState(false);

  const paymentMethods = activeBusiness?.public?.finance?.paymentMethods || [];
  const { currentTasa } = useMoney();
  const mutation = useFinance.useExpenseMutation();

  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<ExpenseFormValues>({
    resolver: zodResolver(expenseFormSchema) as any,
    defaultValues: {
      category: 'other',
      description: '',
      currency: 'USD',
      amount: '' as any, // start empty
      paymentMethodId: '',
      date: format(new Date(), 'yyyy-MM-dd'),
    },
  });

  const watchDate = watch('date');

  useEffect(() => {
    if (open) {
      reset({
        category: 'other',
        description: '',
        currency: 'USD',
        amount: '' as any,
        paymentMethodId: '',
        date: format(new Date(), 'yyyy-MM-dd'),
      });
      setShowFullCalendar(false);
    }
  }, [open, reset]);

  const onSubmit = async (values: ExpenseFormValues) => {
    if (!profile) return;
    
    const selectedMethod = paymentMethods.find(
      (m) => m.id === values.paymentMethodId
    );
    if (!selectedMethod) {
      toast.error('Selecciona un método de pago válido');
      return;
    }

    const isLocalAmount = values.currency === 'BS';
    const amountLocal = isLocalAmount
      ? values.amount
      : values.amount * currentTasa;
    const amountAccounting = isLocalAmount
      ? values.amount / currentTasa
      : values.amount;

    try {
      await mutation.mutateAsync({
        creatorId: profile.id,
        businessId: isWorker ? null : businessId,
        professionalId: isWorker ? profile.id : null,
        category: values.category,
        description: values.description,
        amountLocal,
        amountAccounting,
        paymentMethod: selectedMethod,
        tasa: currentTasa,
        date: values.date ? parseISO(values.date) : new Date(),
      });

      toast.success('Egreso guardado exitosamente');
      onOpenChange(false);
    } catch (error) {
      toast.error('Ocurrió un error al registrar el egreso');
    }
  };

  return (
    <FormSheetLayout
      isOpen={open}
      onOpenChange={onOpenChange}
      heroTitle="Nuevo Egreso"
      heroAction={
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-zinc-100 text-zinc-400">
          <Banknote className="h-6 w-6" strokeWidth={2.5} />
        </div>
      }
      primaryAction={{
        label: 'Confirmar Egreso',
        onClick: handleSubmit((data) => onSubmit(data as any)),
        isLoading: mutation.isPending,
      }}
    >
      <div className="space-y-6 pb-10">
        <div className="space-y-2">
          <label className="ml-2 text-[10px] font-black uppercase tracking-widest text-zinc-400">
            Categoría
          </label>
          <Controller
            control={control}
            name="category"
            render={({ field }) => (
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger className="h-14 rounded-2xl border-none bg-zinc-50 px-6 font-bold shadow-inner focus:ring-zinc-900">
                  <SelectValue placeholder="Selecciona una categoría" />
                </SelectTrigger>
                <SelectContent className="rounded-2xl">
                  {Object.entries(categoryLabels).map(([key, label]) => (
                    <SelectItem key={key} value={key} className="rounded-xl font-medium cursor-pointer">
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
          {errors.category && (
            <p className="ml-2 text-[10px] font-bold text-rose-500">
              {errors.category.message}
            </p>
          )}
        </div>

        <div className="space-y-2">
          <label className="ml-2 text-[10px] font-black uppercase tracking-widest text-zinc-400">
            Descripción
          </label>
          <Input
            {...register('description')}
            placeholder="Ej. Pago de internet, Almuerzo"
            className="h-14 rounded-2xl border-none bg-zinc-50 px-6 font-bold shadow-inner focus-visible:ring-zinc-900"
          />
          {errors.description && (
            <p className="ml-2 text-[10px] font-bold text-rose-500">
              {errors.description.message}
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="ml-2 text-[10px] font-black uppercase tracking-widest text-zinc-400">
              Monto
            </label>
            <Input
              {...register('amount')}
              type="number"
              step="any"
              placeholder="0.00"
              className="h-14 rounded-2xl border-none bg-zinc-50 px-6 font-black tabular-nums shadow-inner focus-visible:ring-zinc-900 text-lg"
            />
            {errors.amount && (
              <p className="ml-2 text-[10px] font-bold text-rose-500">
                {errors.amount.message}
              </p>
            )}
          </div>
          <div className="space-y-2">
            <label className="ml-2 text-[10px] font-black uppercase tracking-widest text-zinc-400">
              Moneda
            </label>
            <Controller
              control={control}
              name="currency"
              render={({ field }) => (
                <Select onValueChange={field.onChange} value={field.value}>
                  <SelectTrigger className="h-14 rounded-2xl border-none bg-zinc-50 px-6 font-black shadow-inner focus:ring-zinc-900 text-lg text-emerald-600">
                    <SelectValue placeholder="Moneda" />
                  </SelectTrigger>
                  <SelectContent className="rounded-2xl">
                    <SelectItem value="USD" className="font-bold text-emerald-600">$ USD</SelectItem>
                    <SelectItem value="BS" className="font-bold">Bs.</SelectItem>
                    <SelectItem value="EUR" className="font-bold">€ EUR</SelectItem>
                    <SelectItem value="COP" className="font-bold text-rose-600">$ COP</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
            {errors.currency && (
              <p className="ml-2 text-[10px] font-bold text-rose-500">
                {errors.currency.message}
              </p>
            )}
          </div>
        </div>

        <div className="space-y-2 flex flex-col pt-2">
          <label className="ml-2 text-[10px] font-black uppercase tracking-widest text-zinc-400">
            Deducir de la Caja
          </label>
          <Controller
            control={control}
            name="paymentMethodId"
            render={({ field }) => (
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger className="h-14 rounded-2xl border-none bg-zinc-100 px-6 font-bold shadow-inner focus:ring-zinc-900 text-zinc-700">
                  <SelectValue placeholder="¿Quién asume el gasto?" />
                </SelectTrigger>
                <SelectContent className="rounded-2xl">
                  {paymentMethods.map((method) => (
                    <SelectItem key={method.id} value={method.id!} className="rounded-xl font-medium cursor-pointer py-3">
                      <div className="flex flex-col text-left">
                        <span className="font-black text-zinc-900">{method.title}</span>
                        <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Saldo en {method.currency}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
          {errors.paymentMethodId && (
            <p className="ml-2 text-[10px] font-bold text-rose-500">
              {errors.paymentMethodId.message}
            </p>
          )}
        </div>

        <div className="space-y-4 pt-2 pb-6">
          <div className="flex items-center justify-between px-1">
            <label className="text-[10px] font-black uppercase tracking-widest text-zinc-400 flex items-center gap-2">
              <CalendarIcon className="h-3 w-3" /> Fecha del Gasto
            </label>
            <button 
              type="button" 
              onClick={() => setShowFullCalendar(!showFullCalendar)}
              className="text-[9px] font-black uppercase text-zinc-500 flex items-center gap-1 hover:text-zinc-900 transition-colors"
            >
              <CalendarIcon className="h-3 w-3" />
              {showFullCalendar ? 'Ver días recientes' : 'Ver calendario'}
            </button>
          </div>

          {showFullCalendar ? (
            <div className="bg-white rounded-[2.5rem] p-4 shadow-sm ring-1 ring-zinc-100 flex justify-center border-2 border-zinc-100 animate-in zoom-in-95 duration-300">
              <Calendar
                selected={watchDate ? parseISO(watchDate) : undefined}
                onSelect={(d) => { 
                  if (d) { 
                    setValue('date', format(d, 'yyyy-MM-dd'), { shouldValidate: true }); 
                    setShowFullCalendar(false);
                  } 
                }}
              />
            </div>
          ) : (
            <div className="flex gap-2 overflow-x-auto pb-4 no-scrollbar -mx-4 px-4">
              {[...Array(7)].map((_, i) => {
                // Retroactivamente desde hoy hasta 6 días atrás
                const day = addDays(startOfDay(new Date()), -i);
                const isSelected = watchDate === format(day, 'yyyy-MM-dd');
                
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => {
                      setValue('date', format(day, 'yyyy-MM-dd'), { shouldValidate: true });
                    }}
                    className={`flex flex-col items-center justify-center min-w-[64px] h-20 rounded-[1.5rem] border-2 transition-all active:scale-95 shrink-0 ${
                      isSelected 
                        ? 'bg-zinc-900 border-zinc-900 text-white shadow-lg' 
                        : 'bg-white border-zinc-100 text-zinc-900 shadow-sm'
                    }`}
                  >
                    <span className="text-[8px] font-black uppercase opacity-50 mb-1">
                      {i === 0 ? 'Hoy' : i === 1 ? 'Ayer' : format(day, 'EEE', { locale: es })}
                    </span>
                    <span className="text-lg font-black tracking-tighter italic leading-none">
                      {format(day, 'd')}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
          {errors.date && (
            <p className="ml-2 text-[10px] font-bold text-rose-500">
              {errors.date.message}
            </p>
          )}
        </div>
      </div>
    </FormSheetLayout>
  );
}
